import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subscribeAuthState, userHasGoogleProvider, userHasPasswordProvider } from "../repositories/authRepository";
import {
  bootstrapUserSession,
  friendlyAuthErrorMessage,
  linkGoogleAccount,
  registerRallyAllyAccount,
  signInWithGoogleAccount,
  signInWithRallyAllyAccount,
  signOutSession,
} from "../services/authService";
import { getCurrentAuthUser } from "../repositories/authRepository";
import {
  readPendingProfileOnboarding,
  writePendingProfileOnboarding,
} from "../domain/auth/profileOnboardingSession";
import { readPendingRosterInviteRoom } from "../domain/auth/rosterJoinSession";
import { hasPendingArenaInvitation } from "../domain/arenaInvitation/pendingArenaInvitation";
import { E2E_AUTH_STUB_ENABLED, createE2eAuthContextValue } from "../app/e2eAuthStub";
import { withTimeout } from "../utils/withTimeout";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  if (E2E_AUTH_STUB_ENABLED) {
    return (
      <AuthContext.Provider value={createE2eAuthContextValue()}>{children}</AuthContext.Provider>
    );
  }

  return <AuthProviderLive>{children}</AuthProviderLive>;
}

function AuthProviderLive({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleLinking, setGoogleLinking] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingProfileOnboarding, setPendingProfileOnboardingState] = useState(
    readPendingProfileOnboarding
  );
  const initialBootDone = useRef(false);

  const setPendingProfileOnboarding = useCallback((pending) => {
    setPendingProfileOnboardingState(pending);
    writePendingProfileOnboarding(pending);
  }, []);

  useEffect(() => {
    let active = true;
    let authStateReceived = false;

    const bootTimeoutMs = 12000;
    const bootTimeout = window.setTimeout(() => {
      if (!active || authStateReceived || initialBootDone.current) return;
      setLoading(false);
      setError(
        new Error(
          "Sign-in is taking too long. Try a hard refresh. On iPhone, close the tab and open TheRallyAlly again. If it keeps happening, clear site data for this URL."
        )
      );
    }, bootTimeoutMs);

    const unsubscribe = subscribeAuthState(async (firebaseUser) => {
      if (!active) return;

      authStateReceived = true;
      window.clearTimeout(bootTimeout);

      const isFirstBoot = !initialBootDone.current;
      if (isFirstBoot) {
        setLoading(true);
      }
      // Always clear stall error once Firebase Auth responds (including after timeout).
      setError(null);

      try {
        if (!firebaseUser) {
          setUser(null);
          setProfile(null);
          return;
        }
        // Firestore IndexedDB can stall on iOS. Identity is enough to paint the shell.
        const boot = await withTimeout(bootstrapUserSession(firebaseUser), 8000);
        if (!active) return;
        setUser(boot.user);
        setProfile(boot.profile);
      } catch (e) {
        if (!active) return;
        // Keep Firebase identity and still render. A profile stall must not
        // leave the boot spinner up forever.
        console.warn("[auth] profile bootstrap did not finish", e);
        setError(null);
        setUser(firebaseUser);
        setProfile(null);
      } finally {
        if (active) {
          initialBootDone.current = true;
          setLoading(false);
        }
      }
    });

    return () => {
      active = false;
      window.clearTimeout(bootTimeout);
      unsubscribe();
    };
  }, []);

  const linkGoogle = useCallback(async () => {
    setGoogleLinking(true);
    setActionError(null);
    try {
      const result = await linkGoogleAccount();
      setUser(result.user);
      setProfile(result.profile);
      return result;
    } catch (e) {
      const err = new Error(friendlyAuthErrorMessage(e));
      setActionError(err);
      throw err;
    } finally {
      setGoogleLinking(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setSigningIn(true);
    setActionError(null);
    try {
      const result = await signInWithGoogleAccount();
      setUser(result.user);
      setProfile(result.profile);
      return result;
    } catch (e) {
      const err = new Error(friendlyAuthErrorMessage(e));
      setActionError(err);
      throw err;
    } finally {
      setSigningIn(false);
    }
  }, []);

  const signInWithRallyAlly = useCallback(async (userId, password) => {
    setSigningIn(true);
    setActionError(null);
    try {
      const result = await signInWithRallyAllyAccount(userId, password);
      setUser(result.user);
      setProfile(result.profile);
      return result;
    } catch (e) {
      const err = new Error(friendlyAuthErrorMessage(e));
      setActionError(err);
      throw err;
    } finally {
      setSigningIn(false);
    }
  }, []);

  const registerRallyAlly = useCallback(async (userId, password) => {
    setSigningIn(true);
    setActionError(null);
    try {
      const result = await registerRallyAllyAccount(userId, password);
      // Persist before setUser so SignInRoute redirect sees the flag even if
      // onAuthStateChanged re-renders first.
      setPendingProfileOnboarding(
        !readPendingRosterInviteRoom() && !hasPendingArenaInvitation()
      );
      setUser(result.user);
      setProfile(result.profile);
      return result;
    } catch (e) {
      const err = new Error(friendlyAuthErrorMessage(e));
      setActionError(err);
      throw err;
    } finally {
      setSigningIn(false);
    }
  }, [setPendingProfileOnboarding]);

  const signOut = useCallback(async () => {
    setSigningOut(true);
    setActionError(null);
    try {
      await signOutSession();
      setUser(null);
      setProfile(null);
      setPendingProfileOnboarding(false);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setActionError(err);
      throw err;
    } finally {
      setSigningOut(false);
    }
  }, []);

  const clearActionError = useCallback(() => setActionError(null), []);

  const completeProfileOnboarding = useCallback(() => {
    setPendingProfileOnboarding(false);
  }, [setPendingProfileOnboarding]);

  const ensureSessionReady = useCallback(async () => {
    if (user?.uid) return user.uid;
    const current = getCurrentAuthUser();
    if (!current?.uid) return null;
    const boot = await bootstrapUserSession(current);
    setUser(boot.user);
    setProfile(boot.profile);
    return boot.user.uid;
  }, [user?.uid]);

  const value = useMemo(
    () => ({
      user,
      profile,
      uid: user?.uid ?? null,
      loading,
      googleLinking,
      signingIn,
      signingOut,
      error,
      actionError,
      clearActionError,
      pendingProfileOnboarding,
      completeProfileOnboarding,
      isAuthenticated: Boolean(user && !user.isAnonymous),
      isAnonymous: Boolean(user?.isAnonymous),
      hasGoogle: userHasGoogleProvider(user),
      hasPassword: userHasPasswordProvider(user),
      linkGoogle,
      signInWithGoogle,
      signInWithRallyAlly,
      registerRallyAlly,
      signOut,
      ensureSessionReady,
    }),
    [
      user,
      profile,
      loading,
      googleLinking,
      signingIn,
      signingOut,
      error,
      actionError,
      clearActionError,
      pendingProfileOnboarding,
      completeProfileOnboarding,
      linkGoogle,
      signInWithGoogle,
      signInWithRallyAlly,
      registerRallyAlly,
      signOut,
      ensureSessionReady,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
